import type { Point, PointArray } from '../types/contour-definition-array-type';

/**
 * Contour propagation across cardiac cine phases.
 *
 * Approach: 1D normal-profile feature tracking.
 * For each contour point we:
 *
 *   1. Sample a short intensity profile along the point's normal.
 *   2. Search a small range along that same normal in the next frame for
 *      the 1D shift that gives the best normalized cross-correlation (NCC).
 *   3. Move the point along its normal only by that shift (tangential
 *      motion along a smooth edge is ambiguous from local intensity alone
 *      — the aperture problem — so we don't try to estimate it).
 *   4. Regularize the whole polyline so noisy per-point matches can't make
 *      it self-intersect or get spiky.
 *
 * Regularization three complementary steps, run every frame:
 *   a. Length-weighted Taubin smoothing — removes point-level match noise.
 *      Weighted by inverse edge length rather than a flat neighbor average,
 *      because contour points do NOT stay evenly spaced across a
 *      propagation (base moves more than apex, septum differently from
 *      lateral wall) — a flat average is a biased curvature estimate once
 *      spacing drifts uneven, and iterating it many times on an uneven
 *      mesh is a real source of wavy/oscillatory artifacts.
 *   b. Re-resample to uniform arc length — corrects the spacing drift at
 *      its source, every step, so (a) stays well-conditioned.
 *   c. Fourier band-limiting — keeps only the first `fourierHarmonics`
 *      frequency components of the closed contour. This is a single
 *      bounded projection, not an iterative diffusion, so applying it
 *      every frame doesn't progressively erode real anatomy (RV insertion
 *      points, septal flattening) the way many Taubin iterations would
 *      over a 20-30 phase propagation. This is the main lever for "should
 *      be round, not wavy" — prefer raising fourierHarmonics down or
 *      smoothIterations down over cranking either one up.
 *
 * Flexible multi-template anchor weighting:
 *   - Dynamic template (immediate previous frame): tracks sequential frame-by-frame changes.
 *   - Rolling / adaptive anchor: updates from each new tracked phase via EMA. Because this
 *     updates from the tracker's own output, a bad match can get reinforced in later frames —
 *     keep rollingAnchorWeight modest and lean on staticAnchorWeight + fourierSmooth as backstops.
 *   - Static anchor template (initial user-drawn reference contour): pulls points back if they
 *     drift off the real anatomical boundary. How well this matches varies a lot around the
 *     contour as anatomy diverges from its phase-1 appearance — keep this modest too, or it
 *     becomes a second source of inconsistent per-point pull rather than a drift backstop.
 *   - Bi-directional forward + backward tracking blended across the periodic cine cycle.
 */

// ----------------------------- config ---------------------------------

export interface ContourPropagationOptions {
  profileHalfLenPx?: number; // profile extends +-profileHalfLenPx along the normal (default 9)
  profileStepPx?: number; // sub-pixel sampling step along the profile (default 0.5)
  searchHalfRangePx?: number; // search +-searchHalfRangePx along the normal in the next frame (default 7 — tune to your actual px/frame motion, see note in propagateAllPhases)
  dynamicWeight?: number; // weight for immediate previous frame (default 1.0)
  staticAnchorWeight?: number; // weight for original reference contour (default 0.15; set 0 for pure dynamic)
  rollingAnchorWeight?: number; // weight for adaptive rolling template (default 0.25)
  rollingAnchorUpdateRate?: number; // learning rate alpha in [0, 1] for rolling template (default 0.3)
  anchorWeight?: number; // legacy alias for staticAnchorWeight — ignored if staticAnchorWeight is set explicitly
  smoothIterations?: number; // taubin iterations per frame step (default 1 — see fourierHarmonics for the primary roundness control)
  taubinLambda?: number; // taubin "relax" factor (default 0.5)
  taubinMu?: number; // taubin "inflate" factor (|mu| > lambda, opposite sign, default -0.53)
  matchIterationsPerFrame?: number; // match -> smooth -> re-match, per frame step (default 2)
  referencePointCount?: number; // point count the reference contour is resampled to (default 90)
  fourierHarmonics?: number; // closed-contour shape band-limit, applied every frame step (default 10; 0 disables). This is a bounded projection, not an iterative diffusion — the main lever for keeping contours round without eroding anatomy.
  reparameterizeEveryStep?: boolean; // re-resample to even arc length every frame step (default true) — keeps the length-weighted smoothing and fourierSmooth well-conditioned
}

export const DEFAULT_CONTOUR_PROPAGATION_OPTIONS: Required<ContourPropagationOptions> = {
  profileHalfLenPx: 15,
  profileStepPx: 0.5,
  searchHalfRangePx: 1.5,
  dynamicWeight: 1.0,
  staticAnchorWeight: 0.25,
  rollingAnchorWeight: 0.25,
  rollingAnchorUpdateRate: 0.35,
  anchorWeight: 0.2,
  smoothIterations: 2,
  taubinLambda: 0.5,
  taubinMu: -0.53,
  matchIterationsPerFrame: 2,
  referencePointCount: 90,
  fourierHarmonics: 5,
  reparameterizeEveryStep: false,
};

// ----------------------------- small helpers ---------------------------

const mod = (a: number, n: number): number => ((a % n) + n) % n;
const avg = (arr: number[]): number => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);

// ----------------------------- image sampling ---------------------------

/** Bilinear sample of a single-channel image stored as a flat typed array. */
export function bilinear(
  image: Float32Array | ArrayLike<number>,
  width: number,
  height: number,
  x: number,
  y: number,
): number {
  if (x < 0 || y < 0 || x > width - 1 || y > height - 1) return 0;
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);
  const fx = x - x0;
  const fy = y - y0;
  const v00 = image[y0 * width + x0];
  const v10 = image[y0 * width + x1];
  const v01 = image[y1 * width + x0];
  const v11 = image[y1 * width + x1];
  const a = v00 + (v10 - v00) * fx;
  const b = v01 + (v11 - v01) * fx;
  return a + (b - a) * fy;
}

// ----------------------------- contour geometry -------------------------

/**
 * Per-point normals of a polyline/contour. Sign convention is consistent point-to-point.
 * Handles both closed polygons and open polylines.
 */
export function computeNormals(points: Point[], isOpen: boolean = false): Point[] {
  const n = points.length;
  if (n === 0) return [];
  if (n === 1) {
    return [{ x: 0, y: 1 }];
  }

  const normals: Point[] = [];
  if (!isOpen) {
    for (let i = 0; i < n; i++) {
      const prev = points[mod(i - 1, n)];
      const next = points[mod(i + 1, n)];
      let tx = next.x - prev.x;
      let ty = next.y - prev.y;
      const len = Math.hypot(tx, ty) || 1;
      tx /= len;
      ty /= len;
      normals.push({ x: ty, y: -tx });
    }
  }
  else {
    for (let i = 0; i < n; i++) {
      let tx: number;
      let ty: number;
      if (i === 0) {
        tx = points[1].x - points[0].x;
        ty = points[1].y - points[0].y;
      }
      else if (i === n - 1) {
        tx = points[n - 1].x - points[n - 2].x;
        ty = points[n - 1].y - points[n - 2].y;
      }
      else {
        tx = points[i + 1].x - points[i - 1].x;
        ty = points[i + 1].y - points[i - 1].y;
      }
      const len = Math.hypot(tx, ty) || 1;
      tx /= len;
      ty /= len;
      normals.push({ x: ty, y: -tx });
    }
  }
  return normals;
}

/**
 * Resample a polyline to `count` evenly-spaced points along its arc length.
 * Removes duplicate loop closures before resampling closed contours.
 */
export function resampleArcLength(
  points: Point[],
  count: number,
  isOpen: boolean = false,
): Point[] {
  if (points.length === 0) return [];
  if (points.length === 1 || count <= 1) {
    return Array.from({ length: Math.max(1, count) }, () => ({ ...points[0] }));
  }

  // Handle closed contours: strip repeated endpoint if present
  let pts = points;
  if (!isOpen && points.length > 1) {
    const first = points[0];
    const last = points[points.length - 1];
    if (Math.abs(first.x - last.x) < 1e-4 && Math.abs(first.y - last.y) < 1e-4) {
      pts = points.slice(0, -1);
    }
  }

  const n = pts.length;
  if (n < 2) {
    return Array.from({ length: count }, () => ({ ...pts[0] }));
  }

  if (!isOpen) {
    const cum: number[] = [0];
    for (let i = 1; i <= n; i++) {
      const a = pts[i - 1];
      const b = pts[i % n];
      cum.push(cum[i - 1] + Math.hypot(b.x - a.x, b.y - a.y));
    }
    const total = cum[n];
    if (total <= 1e-6) {
      return Array.from({ length: count }, () => ({ ...pts[0] }));
    }

    const out: Point[] = [];
    for (let k = 0; k < count; k++) {
      const target = (total * k) / count;
      let i = 1;
      while (i <= n && cum[i] < target) i++;
      i = Math.min(i, n);
      const segStart = cum[i - 1];
      const segEnd = cum[i];
      const t = segEnd > segStart ? (target - segStart) / (segEnd - segStart) : 0;
      const a = pts[i - 1];
      const b = pts[i % n];
      out.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
      });
    }
    return out;
  }
  else {
    const cum: number[] = [0];
    for (let i = 1; i < n; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      cum.push(cum[i - 1] + Math.hypot(b.x - a.x, b.y - a.y));
    }
    const total = cum[n - 1];
    if (total <= 1e-6) {
      return Array.from({ length: count }, () => ({ ...pts[0] }));
    }

    const out: Point[] = [];
    const denom = Math.max(1, count - 1);
    for (let k = 0; k < count; k++) {
      const target = (total * k) / denom;
      let i = 1;
      while (i < n && cum[i] < target) i++;
      i = Math.min(i, n - 1);
      const segStart = cum[i - 1];
      const segEnd = cum[i];
      const t = segEnd > segStart ? (target - segStart) / (segEnd - segStart) : 0;
      const a = pts[i - 1];
      const b = pts[i];
      out.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
      });
    }
    return out;
  }
}

/**
 * One Laplacian relaxation step with a given (possibly negative) alpha,
 * weighted by inverse edge length rather than a flat neighbor average.
 * The flat average is only a correct discretization of curvature when
 * points are evenly spaced; contour points drift out of even spacing
 * across a propagation from real, non-uniform cardiac motion even with
 * periodic resampling between frame steps, so this keeps the step
 * well-behaved between resamples too, instead of over/under-correcting
 * wherever spacing happens to be locally uneven.
 */
export function laplacianStep(
  points: Point[],
  alpha: number,
  isOpen: boolean = false,
): Point[] {
  const n = points.length;
  if (n < 3) return points.map(p => ({ ...p }));

  const relax = (prev: Point, cur: Point, nxt: Point): Point => {
    const dPrev = Math.hypot(cur.x - prev.x, cur.y - prev.y) || 1e-6;
    const dNext = Math.hypot(nxt.x - cur.x, nxt.y - cur.y) || 1e-6;
    const wPrev = 1 / dPrev;
    const wNext = 1 / dNext;
    const wSum = wPrev + wNext;
    const avgX = (prev.x * wPrev + nxt.x * wNext) / wSum;
    const avgY = (prev.y * wPrev + nxt.y * wNext) / wSum;
    return { x: cur.x + alpha * (avgX - cur.x), y: cur.y + alpha * (avgY - cur.y) };
  };

  if (!isOpen) {
    return points.map((p, i) => relax(points[mod(i - 1, n)], p, points[mod(i + 1, n)]));
  }
  else {
    return points.map((p, i) => {
      if (i === 0 || i === n - 1) {
        return { ...p };
      }
      return relax(points[i - 1], p, points[i + 1]);
    });
  }
}

/**
 * Taubin smoothing: alternate a "relax" step (positive lambda) with a
 * slightly larger "inflate" step (negative mu) each iteration.
 */
export function taubinSmooth(
  points: Point[],
  iterations: number,
  lambda: number = 0.5,
  mu: number = -0.53,
  isOpen: boolean = false,
): Point[] {
  let pts = points;
  for (let it = 0; it < iterations; it++) {
    pts = laplacianStep(pts, lambda, isOpen);
    pts = laplacianStep(pts, mu, isOpen);
  }
  return pts;
}

/**
 * Band-limit a closed, evenly arc-length-spaced polyline to its first
 * `harmonics` frequency components (elliptical-Fourier-style smoothing).
 * Unlike repeated Taubin/Laplacian passes, this is a single bounded
 * operation — its effect doesn't compound the more times you apply it
 * across a propagation, so it won't progressively erode real anatomy
 * (RV insertion points, septal flattening) the way many diffusion
 * iterations will over a 20-30 phase run. This is the main lever for
 * "should be round, not wavy" — prefer adjusting this over raising
 * smoothIterations/taubinLambda.
 *
 * Requires evenly-spaced input (call resampleArcLength first, or use it
 * via propagateContourOneStep with reparameterizeEveryStep: true) since
 * the direct DFT below assumes uniform sampling. O(n * harmonics), which
 * is trivial at contour sizes (a few hundred ops for n=90, harmonics=10).
 */
export function fourierSmooth(points: Point[], harmonics: number): Point[] {
  const n = points.length;
  if (n === 0 || harmonics <= 0) return points.map(p => ({ ...p }));

  const reX = new Float64Array(harmonics + 1);
  const imX = new Float64Array(harmonics + 1);
  const reY = new Float64Array(harmonics + 1);
  const imY = new Float64Array(harmonics + 1);

  for (let k = 0; k <= harmonics; k++) {
    let rx = 0;
    let ix = 0;
    let ry = 0;
    let iy = 0;
    for (let t = 0; t < n; t++) {
      const theta = (2 * Math.PI * k * t) / n;
      const c = Math.cos(theta);
      const s = Math.sin(theta);
      rx += points[t].x * c;
      ix -= points[t].x * s;
      ry += points[t].y * c;
      iy -= points[t].y * s;
    }
    reX[k] = rx / n;
    imX[k] = ix / n;
    reY[k] = ry / n;
    imY[k] = iy / n;
  }

  const out: Point[] = [];
  for (let t = 0; t < n; t++) {
    let x = reX[0];
    let y = reY[0];
    for (let k = 1; k <= harmonics; k++) {
      const theta = (2 * Math.PI * k * t) / n;
      const c = Math.cos(theta);
      const s = Math.sin(theta);
      // k and n-k are conjugate pairs for a real signal; doubling captures both.
      x += 2 * (reX[k] * c - imX[k] * s);
      y += 2 * (reY[k] * c - imY[k] * s);
    }
    out.push({ x, y });
  }
  return out;
}

// ----------------------------- 1D profile matching ----------------------

export function sampleProfile(
  image: Float32Array | ArrayLike<number>,
  width: number,
  height: number,
  point: Point,
  normal: Point,
  halfLenPx: number,
  stepPx: number,
): Float32Array {
  const n = Math.round((2 * halfLenPx) / stepPx) + 1;
  const profile = new Float32Array(n);
  for (let k = 0; k < n; k++) {
    const s = -halfLenPx + k * stepPx;
    profile[k] = bilinear(image, width, height, point.x + normal.x * s, point.y + normal.y * s);
  }
  return profile;
}

export function zeroMeanNCC(
  a: Float32Array,
  b: Float32Array,
  offset: number,
  len: number,
): number {
  let sa = 0;
  let sb = 0;
  for (let i = 0; i < len; i++) {
    sa += a[i];
    sb += b[i + offset];
  }
  const ma = sa / len;
  const mb = sb / len;
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < len; i++) {
    const va = a[i] - ma;
    const vb = b[i + offset] - mb;
    num += va * vb;
    da += va * va;
    db += vb * vb;
  }
  return num / (Math.sqrt(da * db) || 1e-6);
}

/**
 * Best 1D shift (in samples, relative to the reference window's center)
 * aligning `refProfile` inside `targetProfile`.
 * Sub-sample accuracy comes from a parabolic fit around the NCC peak.
 */
export function bestShiftNCC(
  refProfile: Float32Array,
  targetProfile: Float32Array,
  maxOffset: number,
): { shiftSamples: number; score: number } {
  const len = refProfile.length;
  let bestOffset = maxOffset;
  let bestScore = -Infinity;
  const scores = new Float32Array(2 * maxOffset + 1);

  for (let o = 0; o <= 2 * maxOffset; o++) {
    const s = zeroMeanNCC(refProfile, targetProfile, o, len);
    scores[o] = s;
    if (s > bestScore) {
      bestScore = s;
      bestOffset = o;
    }
  }

  let shift = bestOffset - maxOffset;
  if (bestOffset > 0 && bestOffset < 2 * maxOffset) {
    const sL = scores[bestOffset - 1];
    const sC = scores[bestOffset];
    const sR = scores[bestOffset + 1];
    const denom = sL - 2 * sC + sR;
    if (Math.abs(denom) > 1e-8) {
      shift += Math.max(-1, Math.min(1, (0.5 * (sL - sR)) / denom));
    }
  }

  return { shiftSamples: shift, score: bestScore };
}

// ----------------------------- per-point / per-frame tracking -----------

/**
 * Track one contour point from `prevImage` into `nextImage`.
 * Blends estimates from:
 *   - Dynamic predecessor frame (dynamicWeight)
 *   - Rolling/adaptive moving anchor (rollingAnchorWeight)
 *   - Static initial reference frame (staticAnchorWeight)
 */
export function trackPointToNextFrame(
  prevImage: Float32Array | ArrayLike<number>,
  nextImage: Float32Array | ArrayLike<number>,
  width: number,
  height: number,
  point: Point,
  normal: Point,
  staticAnchorProfile: Float32Array | null,
  rollingAnchorProfile: Float32Array | null,
  opts: Required<ContourPropagationOptions>,
): { x: number; y: number; confidence: number } {
  const {
    profileHalfLenPx,
    profileStepPx,
    searchHalfRangePx,
    dynamicWeight,
    staticAnchorWeight,
    rollingAnchorWeight,
  } = opts;
  const maxOffsetSamples = Math.round(searchHalfRangePx / profileStepPx);

  // 1. Dynamic template from previous frame
  const refProfile = sampleProfile(
    prevImage,
    width,
    height,
    point,
    normal,
    profileHalfLenPx,
    profileStepPx,
  );
  const targetProfile = sampleProfile(
    nextImage,
    width,
    height,
    point,
    normal,
    profileHalfLenPx + searchHalfRangePx,
    profileStepPx,
  );

  const dyn = bestShiftNCC(refProfile, targetProfile, maxOffsetSamples);
  let totalShiftWeighted = Math.max(0, dyn.score) * dynamicWeight * dyn.shiftSamples;
  let totalWeight = Math.max(0, dyn.score) * dynamicWeight;
  let confidence = Math.max(0, dyn.score);

  // 2. Rolling adaptive anchor template (dynamically updated from recent phases).
  // Because this updates from the tracker's own output, a bad match can get
  // reinforced in later frames rather than corrected — keep rollingAnchorWeight
  // modest relative to dynamicWeight.
  if (rollingAnchorProfile && rollingAnchorWeight > 0) {
    const rol = bestShiftNCC(rollingAnchorProfile, targetProfile, maxOffsetSamples);
    const wRol = Math.max(0, rol.score) * rollingAnchorWeight;
    if (wRol > 1e-6) {
      totalShiftWeighted += wRol * rol.shiftSamples;
      totalWeight += wRol;
      confidence = Math.max(confidence, Math.max(0, rol.score));
    }
  }

  // 3. Static initial reference anchor template (resists multi-cycle drift)
  const effStaticWeight = staticAnchorWeight ?? opts.anchorWeight ?? 0;
  if (staticAnchorProfile && effStaticWeight > 0) {
    const anc = bestShiftNCC(staticAnchorProfile, targetProfile, maxOffsetSamples);
    const wAnc = Math.max(0, anc.score) * effStaticWeight;
    if (wAnc > 1e-6) {
      totalShiftWeighted += wAnc * anc.shiftSamples;
      totalWeight += wAnc;
      confidence = Math.max(confidence, Math.max(0, anc.score));
    }
  }

  let finalShiftSamples = dyn.shiftSamples;
  if (totalWeight > 1e-6) {
    finalShiftSamples = totalShiftWeighted / totalWeight;
  }

  // Low-confidence matches move less instead of being hard-frozen
  const trust = Math.max(0, Math.min(1, confidence));
  const shiftPx = finalShiftSamples * profileStepPx * trust;

  return {
    x: point.x + normal.x * shiftPx,
    y: point.y + normal.y * shiftPx,
    confidence: trust,
  };
}

/**
 * One frame step for the whole contour: match -> smooth -> reparameterize
 * -> band-limit, `opts.matchIterationsPerFrame` times.
 */
export function propagateContourOneStep(
  prevImage: Float32Array | ArrayLike<number>,
  nextImage: Float32Array | ArrayLike<number>,
  width: number,
  height: number,
  contour: Point[],
  staticAnchorProfiles: Float32Array[] | null,
  rollingAnchorProfiles: Float32Array[] | null,
  opts: Required<ContourPropagationOptions>,
  isOpen: boolean = false,
): { points: Point[]; confidences: number[] } {
  let pts = contour.map(p => ({ x: p.x, y: p.y }));
  const pointCount = pts.length;
  const confidences: number[] = Array.from<number>({ length: pointCount }).fill(1);

  for (let iter = 0; iter < opts.matchIterationsPerFrame; iter++) {
    const normals = computeNormals(pts, isOpen);
    const moved: Point[] = [];
    for (let i = 0; i < pts.length; i++) {
      const res = trackPointToNextFrame(
        prevImage,
        nextImage,
        width,
        height,
        pts[i],
        normals[i],
        staticAnchorProfiles ? staticAnchorProfiles[i] : null,
        rollingAnchorProfiles ? rollingAnchorProfiles[i] : null,
        opts,
      );
      moved.push({ x: res.x, y: res.y });
      confidences[i] = res.confidence;
    }
    pts = taubinSmooth(moved, opts.smoothIterations, opts.taubinLambda, opts.taubinMu, isOpen);

    // Re-establish uniform arc-length spacing before the next round of
    // matching/smoothing. Real cardiac motion isn't uniform along the
    // contour, so points drift out of even spacing purely from anatomy —
    // and the length-weighted smoothing above stays best-conditioned when
    // this is corrected regularly rather than left to accumulate.
    if (opts.reparameterizeEveryStep) {
      pts = resampleArcLength(pts, pointCount, isOpen);
    }

    // Bounded, non-compounding shape regularization — see fourierSmooth
    // doc comment. Closed contours only; DFT assumes periodicity.
    if (!isOpen && opts.fourierHarmonics > 0) {
      pts = fourierSmooth(pts, opts.fourierHarmonics);
    }
  }

  return { points: pts, confidences };
}

/**
 * Update rolling anchor profiles using exponential moving average (EMA)
 * from the newly tracked contour on the next frame.
 */
export function updateRollingProfiles(
  nextImage: Float32Array | ArrayLike<number>,
  width: number,
  height: number,
  points: Point[],
  currentRollingProfiles: Float32Array[],
  alpha: number,
  opts: Required<ContourPropagationOptions>,
  isOpen: boolean = false,
): Float32Array[] {
  if (alpha <= 0) return currentRollingProfiles;
  const normals = computeNormals(points, isOpen);
  const updated: Float32Array[] = [];

  for (let i = 0; i < points.length; i++) {
    const newProf = sampleProfile(
      nextImage,
      width,
      height,
      points[i],
      normals[i],
      opts.profileHalfLenPx,
      opts.profileStepPx,
    );
    const oldProf = currentRollingProfiles[i];
    const blended = new Float32Array(newProf.length);
    for (let k = 0; k < newProf.length; k++) {
      blended[k] = (1 - alpha) * oldProf[k] + alpha * newProf[k];
    }
    updated.push(blended);
  }

  return updated;
}

// ----------------------------- top-level driver --------------------------

export interface FrameDataInput {
  data: Float32Array | ArrayLike<number>;
  width: number;
  height: number;
}

export interface PropagationResult {
  contours: PointArray[];
  confidences: number[];
}

/**
 * Propagate a reference contour across a full cine phase stack.
 *
 * A note on searchHalfRangePx: this bounds how much per-frame motion the
 * tracker can ever correct, along the normal. Don't guess it — derive a
 * starting value from your acquisition: expected peak wall velocity (cm/s)
 * x frame interval (s) / pixel spacing (mm/px), plus margin. At typical
 * SAX cine resolution and temporal sampling this is usually several
 * pixels, not one — a value that's too small will silently cap tracking
 * well below the true motion every single frame, which compounds across
 * the whole propagation.
 *
 * @param frames Array of { data: Float32Array, width, height }, one per cardiac phase, in chronological order.
 * @param referenceContour Points drawn by the user on frames[referencePhaseIndex].
 * @param referencePhaseIndex Index in frames corresponding to the user reference contour (e.g. current phase).
 * @param userOpts Tracking, weighting and regularization options.
 * @param isOpen Whether the contour is an open polyline instead of a closed polygon.
 * @returns { contours, confidences } — one contour + one avg confidence per phase.
 */
export function propagateAllPhases(
  frames: FrameDataInput[],
  referenceContour: Point[],
  referencePhaseIndex: number,
  userOpts: ContourPropagationOptions = {},
  isOpen: boolean = false,
): PropagationResult {
  const opts: Required<ContourPropagationOptions> = {
    ...DEFAULT_CONTOUR_PROPAGATION_OPTIONS,
    ...userOpts,
  };
  // If user passed legacy anchorWeight, normalize staticAnchorWeight
  if (userOpts.anchorWeight !== undefined && userOpts.staticAnchorWeight === undefined) {
    opts.staticAnchorWeight = userOpts.anchorWeight;
  }

  const N = frames.length;
  if (N === 0) {
    return { contours: [], confidences: [] };
  }
  if (N === 1) {
    return { contours: [[...referenceContour]], confidences: [1] };
  }

  const { width, height } = frames[0];
  const refImage = frames[referencePhaseIndex].data;

  const refPts = resampleArcLength(referenceContour, opts.referencePointCount, isOpen);
  const refNormals = computeNormals(refPts, isOpen);
  // NOTE: anchor profile index i corresponds to arc-length position i on the
  // reference contour. Because reparameterizeEveryStep re-resamples by arc
  // length every frame too, index i on later phases stays an approximate
  // (not exact) correspondence to the same anatomical location — a small,
  // self-correcting drift, unlike the spacing-drift problem this is meant
  // to fix, which otherwise compounds unbounded over the propagation.
  const staticAnchorProfiles = refPts.map((p, i) =>
    sampleProfile(refImage, width, height, p, refNormals[i], opts.profileHalfLenPx, opts.profileStepPx),
  );

  const forward: Point[][] = Array.from({ length: N }, () => []);
  const backward: Point[][] = Array.from({ length: N }, () => []);
  const forwardConf: number[] = Array.from<number>({ length: N }).fill(1);
  const backwardConf: number[] = Array.from<number>({ length: N }).fill(1);
  forward[referencePhaseIndex] = refPts;
  backward[referencePhaseIndex] = refPts;

  // Forward: ref -> ref+1 -> ... -> ref-1 (sequential cine loop)
  let curFwd = refPts;
  let rollingFwd: Float32Array[] = staticAnchorProfiles.map(p => new Float32Array(p));
  for (let step = 1; step < N; step++) {
    const prevIdx = mod(referencePhaseIndex + step - 1, N);
    const nextIdx = mod(referencePhaseIndex + step, N);
    const { points, confidences } = propagateContourOneStep(
      frames[prevIdx].data,
      frames[nextIdx].data,
      width,
      height,
      curFwd,
      staticAnchorProfiles,
      rollingFwd,
      opts,
      isOpen,
    );
    forward[nextIdx] = points;
    forwardConf[nextIdx] = avg(confidences);
    curFwd = points;

    // Dynamically update the rolling anchor memory from the new phase
    if (opts.rollingAnchorWeight > 0 && opts.rollingAnchorUpdateRate > 0) {
      rollingFwd = updateRollingProfiles(
        frames[nextIdx].data,
        width,
        height,
        points,
        rollingFwd,
        opts.rollingAnchorUpdateRate,
        opts,
        isOpen,
      );
    }
  }

  // Backward: ref -> ref-1 (wraps to N-1, cine is periodic) -> ref-2 -> ... -> ref+1
  let curBwd = refPts;
  let rollingBwd: Float32Array[] = staticAnchorProfiles.map(p => new Float32Array(p));
  for (let step = 1; step < N; step++) {
    const prevIdx = mod(referencePhaseIndex - step + 1, N);
    const nextIdx = mod(referencePhaseIndex - step, N);
    const { points, confidences } = propagateContourOneStep(
      frames[prevIdx].data,
      frames[nextIdx].data,
      width,
      height,
      curBwd,
      staticAnchorProfiles,
      rollingBwd,
      opts,
      isOpen,
    );
    backward[nextIdx] = points;
    backwardConf[nextIdx] = avg(confidences);
    curBwd = points;

    // Dynamically update the rolling anchor memory from the new phase
    if (opts.rollingAnchorWeight > 0 && opts.rollingAnchorUpdateRate > 0) {
      rollingBwd = updateRollingProfiles(
        frames[nextIdx].data,
        width,
        height,
        points,
        rollingBwd,
        opts.rollingAnchorUpdateRate,
        opts,
        isOpen,
      );
    }
  }

  // Blend, weighted by inverse path length from the anchor phase
  const contours: PointArray[] = [];
  const confidences: number[] = [];
  for (let idx = 0; idx < N; idx++) {
    if (idx === referencePhaseIndex) {
      contours.push(refPts);
      confidences.push(1);
      continue;
    }
    const fwdSteps = mod(idx - referencePhaseIndex, N);
    const bwdSteps = N - fwdSteps;
    const wF = 1 / fwdSteps;
    const wB = 1 / bwdSteps;
    const blended = forward[idx].map((p, i) => ({
      x: (wF * p.x + wB * backward[idx][i].x) / (wF + wB),
      y: (wF * p.y + wB * backward[idx][i].y) / (wF + wB),
    }));
    let out = taubinSmooth(blended, 1, opts.taubinLambda, opts.taubinMu, isOpen);
    if (!isOpen && opts.fourierHarmonics > 0) {
      out = fourierSmooth(resampleArcLength(out, out.length, isOpen), opts.fourierHarmonics);
    }
    contours.push(out);
    confidences.push((wF * forwardConf[idx] + wB * backwardConf[idx]) / (wF + wB));
  }

  return { contours, confidences };
}
